import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { useTenantBranding } from "@/api/queries/branding";
import { useBusinessInfo } from "@/api/queries/businessInfo";
import { usePublicLegalIndex } from "@/api/queries/legal";
import { usePublicPlans } from "@/api/queries/publicPlans";
import { usePublicSubprocessors } from "@/api/queries/subprocessors";
import { pickI18nBlob } from "@/components/admin/I18nField";
import { PageHero } from "@/components/ui/PageHero";
import { Pill } from "@/components/ui/Pill";
import { entitlementAvailability, type Availability } from "@/lib/entitlementAvailability";
import { legalVersionNumber } from "@/lib/labels";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useSeo } from "@/lib/seo";

/**
 * `/security` on the PLATFORM shell — "what happens to my data, and
 * what happens when you go down".
 *
 * Every answer this page gives already existed; they were scattered
 * across the legal pages, where a shop owner comparing tools does not
 * look. Nothing here is new policy. What IS new is the rule the page
 * lives by: **no claim is written into the copy.** The subprocessor
 * count, the DPA version, which plan first grants single sign-on, the
 * region the cluster runs in — each is read at request time from the
 * thing that decides it.
 *
 * That rule is the whole point. A security page is exactly the kind of
 * page nobody revisits, so a sentence typed here would still be
 * claiming "seven subprocessors" long after there were nine. A row
 * whose source cannot answer renders nothing rather than a stale
 * number.
 *
 * **Backups, recovery, encryption, incidents (KAN-1938).** These rows
 * restate, in plain words, what the data processing agreement already
 * promises (Annex 2 and Art. 8.2): HTTPS, password hashes, daily
 * encrypted backups outside the hosting provider (30 days daily, 90
 * days monthly — `services/db_backup.py`), recovery within 4 hours
 * losing at most 24 hours (`docs/DR-RUNBOOK.md`). They are copy, not
 * read from a source, because the source is a published contract
 * text; if the DPA changes, this copy must change with it.
 *
 * **Deliberately absent — an uptime promise.** An availability promise
 * is a contractual claim; the live status page is linked instead, which
 * shows the record rather than promising one. Certifications (ISO, SOC)
 * are out of scope for the simplest possible reason: we do not have
 * them.
 */
/**
 * Doba uchování záloh — `DAILY_RETENTION_DAYS` / `MONTHLY_RETENTION_DAYS`
 * v `apps/api/app/services/db_backup.py`, slíbené v DPA (čl. 9.3,
 * příloha 2). Do překladu jdou jako parametr: guard
 * `tests/platformLandingCopy.test.ts` nepouští počty dní psané v textu.
 * Změní-li se lhůta v kódu záloh, musí se změnit i tady a v DPA.
 */
const BACKUP_DAILY_RETENTION_DAYS = 30;
const BACKUP_MONTHLY_RETENTION_DAYS = 90;

export default function Security(): JSX.Element {
  const { t } = useTranslation("saas");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  useSeo({
    title: t("security.seo.title"),
    description: t("security.seo.description"),
    path: "/security",
    lang,
  });

  const branding = useTenantBranding();
  const businessInfo = useBusinessInfo();
  const legal = usePublicLegalIndex(lang);
  const plans = usePublicPlans();
  const subprocessors = usePublicSubprocessors();

  const region = branding.data?.platform_region ?? null;
  // Provozovatel platformy, ne demo obchod za platformním hostem —
  // stejný zdroj jako patička (`GET /business-info`).
  const supportEmail = businessInfo.data?.support_email || null;
  const dpa = (legal.data?.items ?? []).find((item) => item.kind_key === "dpa");
  // Jen aktivní: od KAN-1937 seznam vrací i vyřazené zpracovatele
  // (se stavem), a ti se do „kolik jich dnes používáme" nepočítají.
  // KAN-1938 — a jen skuteční zpracovatelé: ARES ani přihlášení přes
  // Google pro nás osobní údaje nezpracovávají.
  const subprocessorCount =
    subprocessors.data?.items.filter((item) => item.is_active && item.is_processor)
      .length ?? 0;
  const planCards = plans.data?.items ?? [];

  const availabilityText = (key: string): string | null => {
    const availability: Availability = entitlementAvailability(planCards, key);
    if (availability.kind === "all") return t("security.availability.all");
    if (availability.kind === "none") return t("security.availability.none");
    return t("security.availability.from", {
      plan: pickI18nBlob(availability.plan.name_i18n, availability.plan.name, availability.plan.name_en, lang),
    });
  };

  // A row renders only when its source answered. The alternative is a
  // page that says "single sign-on: —" while the catalogue loads, which
  // reads as "not offered" rather than "not yet known".
  const rows: {
    key: string;
    detail: string | null;
    link?: { to: string; label: string };
  }[] = [
    {
      // KAN-1938 — místo „data v EU": kde je databáze a soubory, a že
      // část dodavatelů je z USA. Region se čte z konfigurace nasazení;
      // lidský název místa dodá překlad, neznámý kód se vypíše holý.
      key: "residency",
      detail: region
        ? t("security.rows.residency.detail", {
            place: t(`security.regions.${region}`, { defaultValue: region }),
          })
        : null,
      link: {
        to: `/${lang}/legal/subprocessors`,
        label: t("security.rows.residency.link"),
      },
    },
    {
      key: "subprocessors",
      detail: subprocessorCount
        ? t("security.rows.subprocessors.detail", { count: subprocessorCount })
        : null,
      link: {
        to: `/${lang}/legal/subprocessors`,
        label: t("security.rows.subprocessors.link"),
      },
    },
    { key: "encryption", detail: t("security.rows.encryption.detail") },
    {
      key: "backups",
      detail: t("security.rows.backups.detail", {
        dailyDays: BACKUP_DAILY_RETENTION_DAYS,
        monthlyDays: BACKUP_MONTHLY_RETENTION_DAYS,
      }),
    },
    { key: "recovery", detail: t("security.rows.recovery.detail") },
    { key: "access", detail: t("security.rows.access.detail") },
    { key: "incidents", detail: t("security.rows.incidents.detail") },
    {
      key: "dpa",
      detail: dpa ? t("security.rows.dpa.detail", {
            version: legalVersionNumber(dpa.version),
          }) : null,
      link: dpa
        ? { to: `/${lang}/${dpa.slug}`, label: t("security.rows.dpa.link") }
        : undefined,
    },
    {
      key: "export",
      detail: planCards.length ? availabilityText("data.gdpr_export") : null,
    },
    {
      key: "audit",
      detail: planCards.length ? availabilityText("audit_log") : null,
    },
    {
      key: "sso",
      detail: planCards.length ? availabilityText("sso.saml") : null,
    },
    {
      // No entitlement gates two-factor auth and none should. Stated
      // flatly because there is no per-plan source to read. Mandatory
      // for admin and production roles: `enforce_2fa_for_roles`.
      key: "twoFactor",
      detail: t("security.rows.twoFactor.detail"),
    },
  ];

  return (
    <>
      <PageHero innerClassName="max-w-4xl px-7 pb-12 pt-12 md:pt-16">
        <Pill tone="accent">{t("security.eyebrow")}</Pill>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-ink md:text-4xl">
          {t("security.title")}
        </h1>
        <p className="mt-3 max-w-2xl text-ink-2">{t("security.lead")}</p>
      </PageHero>

      <main className="mx-auto max-w-4xl px-7 pb-20 pt-12">
        <dl className="grid gap-5" data-testid="security-rows">
          {rows
            .filter((row) => row.detail !== null)
            .map((row) => (
              <div
                key={row.key}
                className="rounded-lg border border-border bg-surface p-5"
                data-testid={`security-row-${row.key}`}
              >
                <dt className="text-base font-semibold text-ink">
                  {t(`security.rows.${row.key}.title`)}
                </dt>
                <dd className="mt-2 text-sm text-ink-2">
                  {row.detail}
                  {row.link ? (
                    <>
                      {" "}
                      <Link
                        to={row.link.to}
                        className="underline underline-offset-2 hover:text-ink"
                      >
                        {row.link.label}
                      </Link>
                    </>
                  ) : null}
                </dd>
              </div>
            ))}
        </dl>

        <StatusNote />

        {supportEmail ? (
          <p className="mt-8 text-sm text-ink-2" data-testid="security-vulnerability">
            {t("security.vulnerability")}{" "}
            <a
              href={`mailto:${supportEmail}`}
              className="underline underline-offset-2 hover:text-ink"
            >
              {supportEmail}
            </a>
          </p>
        ) : null}

        <p className="mt-10 text-sm text-ink-3">
          {t("security.contactNote")}{" "}
          <Link
            to={`/${lang}/contact`}
            className="underline underline-offset-2 hover:text-ink"
          >
            {t("security.contactLink")}
          </Link>
        </p>
      </main>
    </>
  );
}

/**
 * The live status page, linked rather than summarised.
 *
 * It lives outside this repository and outside our infrastructure — on
 * purpose, since a status page hosted on the thing it reports about is
 * worthless during the only outage that matters. Its address is a
 * build-time variable, so an installation without one renders nothing
 * instead of a dead link.
 */
function StatusNote(): JSX.Element | null {
  const { t } = useTranslation("saas");
  const statusUrl = import.meta.env.VITE_STATUS_URL;
  if (!statusUrl) return null;
  return (
    <p
      className="mt-8 rounded-lg border border-dashed border-border bg-surface p-5 text-sm text-ink-2"
      data-testid="security-status"
    >
      {t("security.statusNote")}{" "}
      <a
        href={statusUrl}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2 hover:text-ink"
      >
        {t("security.statusLink")}
      </a>
    </p>
  );
}
